import { useEffect, useMemo, useState } from "react";
import { OrganizationResponse } from "../../models/OrganizationModels";
import { User } from "../../models/User";
import { RoleType } from "../../models/UserModels";
import { resolveImageUrl } from "../../utils/ImageUrl";
import "./UserFormModal.css";

export interface UserFormValues {
  email: string;
  userName: string;
  contact: string;
  organizationId: string;
  role: RoleType;
  order: number;
  designation: string;
  qualification: string;
  bio: string;
  isPublic: boolean;
  profilePictureFile: File | null;
}

interface UserFormModalProps {
  isOpen: boolean;
  mode: "create" | "edit";
  submitting: boolean;
  organizations: OrganizationResponse[];
  defaultOrganizationId?: string;
  user?: User | null;
  onClose: () => void;
  onSubmit: (form: UserFormValues) => Promise<void>;
}

const ROLE_OPTIONS: { value: RoleType; label: string }[] = [
  { value: RoleType.OrganizationOwner, label: "Organization Owner" },
  { value: RoleType.Admin, label: "Admin" },
  { value: RoleType.Manager, label: "Manager" },
  { value: RoleType.Receptionist, label: "Receptionist" },
  { value: RoleType.Staff, label: "Staff" },
];

const roleFromName = (name?: string | null) =>
  ROLE_OPTIONS.find((role) => role.label === name)?.value ??
  ROLE_OPTIONS[0].value;

const PROFILE_PICTURE_MAX_BYTES = 150 * 1024;

const UserFormModal = ({
  isOpen,
  mode,
  submitting,
  organizations,
  defaultOrganizationId,
  user,
  onClose,
  onSubmit,
}: UserFormModalProps) => {
  const [form, setForm] = useState<UserFormValues>({
    email: "",
    userName: "",
    contact: "",
    organizationId: "",
    role: ROLE_OPTIONS[0].value,
    order: 0,
    designation: "",
    qualification: "",
    bio: "",
    profilePictureFile: null,
    isPublic: true,
  });
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setFileError(null);
    if (mode === "create") {
    setForm({
      email: "",
      userName: "",
      contact: "",
      organizationId: defaultOrganizationId ?? organizations[0]?.id ?? "",
      role: ROLE_OPTIONS[0].value,
      order: 0,
      designation: "",
      qualification: "",
      bio: "",
      isPublic: true,
      profilePictureFile: null,
    });
  } else {
    setForm({
      email: user?.email ?? "",
      userName: user?.userName ?? "",
      contact: "",
      organizationId: defaultOrganizationId ?? "",
      role: roleFromName(user?.roleName),
      order: user?.order ?? 0,
      designation: user?.designation ?? "",
      qualification: user?.qualification ?? "",
      bio: user?.bio ?? "",
      isPublic: user?.isPublic ?? true,
      profilePictureFile: null,
    });
    }
  }, [isOpen, mode, user, defaultOrganizationId, organizations]);

  const profilePreview = useMemo(
    () =>
      form.profilePictureFile
        ? URL.createObjectURL(form.profilePictureFile)
        : user?.profilePictureUrl
          ? resolveImageUrl(user.profilePictureUrl)
          : undefined,
    [form.profilePictureFile, user?.profilePictureUrl],
  );

  useEffect(() => {
    if (!profilePreview?.startsWith("blob:")) return;
    const url = profilePreview;
    return () => URL.revokeObjectURL(url);
  }, [profilePreview]);

  if (!isOpen) return null;

  const setField = <K extends keyof UserFormValues>(
    key: K,
    value: UserFormValues[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleProfileFileChange = (file: File | null) => {
    setForm((prev) => ({ ...prev, profilePictureFile: file }));
    if (file && file.size > PROFILE_PICTURE_MAX_BYTES) {
      setFileError(
        `Profile picture must be 150 KB or less (selected file is ${Math.round(file.size / 1024)} KB).`,
      );
    } else {
      setFileError(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      form.profilePictureFile &&
      form.profilePictureFile.size > PROFILE_PICTURE_MAX_BYTES
    ) {
      setFileError(
        `Profile picture must be 150 KB or less (selected file is ${Math.round(form.profilePictureFile.size / 1024)} KB).`,
      );
      return;
    }
    onSubmit(form);
  };

  const organizationLocked =
    Boolean(defaultOrganizationId) || mode === "edit";

  return (
    <div className="ufm-overlay">
      <div className="ufm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ufm-modal__header">
          <h3>{mode === "create" ? "Invite User" : "Edit User"}</h3>
          <button
            className="ufm-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="ufm-modal__body" onSubmit={handleSubmit}>
          <div className="ufm-form-group">
            <label htmlFor="ufm-email">Email *</label>
            <input
              id="ufm-email"
              type="email"
              required
              disabled={mode === "edit"}
              placeholder="user@example.com"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
            />
          </div>

          <div className="ufm-form-group">
            <label htmlFor="ufm-userName">Name</label>
            <input
              id="ufm-userName"
              type="text"
              placeholder="Optional"
              value={form.userName}
              onChange={(e) => setField("userName", e.target.value)}
            />
          </div>

          {mode === "create" && (
            <div className="ufm-form-group">
              <label htmlFor="ufm-contact">Contact</label>
              <input
                id="ufm-contact"
                type="text"
                placeholder="Optional"
                value={form.contact}
                onChange={(e) => setField("contact", e.target.value)}
              />
            </div>
          )}

          <div className="ufm-form-group">
            <label htmlFor="ufm-org">Organization *</label>
            {organizationLocked ? (
              <input
                id="ufm-org"
                type="text"
                value={
                  organizations.find(
                    (org) => org.id === defaultOrganizationId,
                  )?.name ?? "This organization"
                }
                disabled
              />
            ) : (
              <select
                id="ufm-org"
                required
                value={form.organizationId}
                onChange={(e) => setField("organizationId", e.target.value)}
              >
                <option value="" disabled>
                  Select an organization
                </option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="ufm-form-group">
            <label htmlFor="ufm-role">Role</label>
            <select
              id="ufm-role"
              value={form.role}
              onChange={(e) => setField("role", Number(e.target.value) as RoleType)}
            >
              {ROLE_OPTIONS.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          <div className="ufm-form-group">
            <label htmlFor="ufm-order">Order</label>
            <input
              id="ufm-order"
              type="number"
              min={0}
              placeholder="Optional"
              value={form.order}
              onChange={(e) => setField("order", Number(e.target.value))}
            />
          </div>

          <div className="ufm-form-group">
            <label htmlFor="ufm-designation">Designation</label>
            <input
              id="ufm-designation"
              type="text"
              placeholder="Optional"
              value={form.designation}
              onChange={(e) => setField("designation", e.target.value)}
            />
          </div>

          <div className="ufm-form-group">
            <label htmlFor="ufm-qualification">Qualification</label>
            <input
              id="ufm-qualification"
              type="text"
              placeholder="Optional"
              value={form.qualification}
              onChange={(e) => setField("qualification", e.target.value)}
            />
          </div>

          <div className="ufm-form-group">
            <label htmlFor="ufm-bio">Bio</label>
            <textarea
              id="ufm-bio"
              rows={3}
              placeholder="Optional"
              value={form.bio}
              onChange={(e) => setField("bio", e.target.value)}
            />
          </div>

          <div className="ufm-form-group ufm-form-group--switch">
            <label className="ufm-switch">
              <input
                id="ufm-isPublic"
                type="checkbox"
                checked={form.isPublic}
                onChange={(e) => setField("isPublic", e.target.checked)}
              />
              <span className="ufm-switch__track" aria-hidden="true" />
              <span className="ufm-switch__label">Show on website</span>
            </label>
            <p className="ufm-hint">
              When on, this member appears in the public team section.
            </p>
          </div>

          <div className="ufm-form-group">
            <label htmlFor="ufm-profile">Profile Picture</label>
            {profilePreview && (
              <img
                className="ufm-preview"
                src={profilePreview}
                alt="Profile preview"
              />
            )}
            <input
              id="ufm-profile"
              type="file"
              accept="image/*"
              onChange={(e) => handleProfileFileChange(e.target.files?.[0] ?? null)}
            />
            <p className="ufm-hint">
              Max 150 KB. Preview shows the member photo crop (4:5 portrait).
            </p>
            {fileError && <p className="ufm-error">{fileError}</p>}
          </div>

          <div className="ufm-modal__actions">
            <button
              type="button"
              className="ufm-btn ufm-btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ufm-btn ufm-btn--primary"
              disabled={submitting || !!fileError}
            >
              {submitting
                ? mode === "create"
                  ? "Sending..."
                  : "Saving..."
                : mode === "create"
                  ? "Send Invitation"
                  : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;